/**
 * Table component constants
 * Extracted from ProfessionalTable.tsx for maintainability
 */

// Base columns that should not have enrichment options
export const LEAD_TYPE_BASE_COLUMNS: Record<'companies' | 'people' | 'custom', string[]> = {
  companies: ['Name', 'Description', 'URL'],
  people: ['Name', 'Company', 'Job Title', 'URL'],
  custom: ['Name', 'Description', 'URL']
};

// Column width constants (in pixels)
export const COLUMN_WIDTHS = {
  NAME: 180,
  URL: 220,
  EMAIL: 220,
  DEFAULT: 150,
  SEGMENT: 140,
  MONITORED: 110,
  ROW_NUMBER: 40,
  ADD_COLUMN_SPACER: 48,
  MIN_SEGMENT: 140,
  MAX_SEGMENT: 300,
  MIN_RESIZE: 100,
} as const;

// Format presets for column enrichment
export const FORMAT_PRESETS = [
  { value: 'none', label: 'No specific format', hint: '' },
  { value: 'sentence', label: 'One concise sentence', hint: 'Respond in one concise sentence' },
  { value: 'tagline', label: 'Short tagline', hint: 'Respond as a short tagline' },
  { value: 'date', label: 'Date (YYYY-MM-DD)', hint: 'Respond in date format (YYYY-MM-DD)' },
  { value: 'summary', label: 'Brief summary (max 2 lines)', hint: 'Respond as a brief summary (max 2 lines)' },
  { value: 'custom', label: 'Custom format', hint: '' },
] as const;

export type FormatPresetValue = typeof FORMAT_PRESETS[number]['value'];

// Get format hint by preset value
export const getFormatHint = (preset: string): string => {
  const found = FORMAT_PRESETS.find(p => p.value === preset);
  return found?.hint || '';
};

// Performance thresholds
export const PERFORMANCE_WARNING_THRESHOLD = 800;

// Timing constants (in milliseconds)
export const TABLE_TIMINGS = {
  PROGRESS_POLL_INTERVAL: 5000,
  INITIAL_PROGRESS_DELAY: 3000,
  HOVER_PREVIEW_DEBOUNCE: 180,
  HOVER_PREVIEW_HIDE_DELAY: 200,
  SEARCH_FOCUS_DELAY: 50,
  CHAT_ACTION_DELAY: 100,
  ENRICHMENT_PROGRESS_UPDATE_INTERVAL: 1000,
} as const;

// Z-index hierarchy for table components
export const TABLE_Z_INDEX = {
  CELL: 2,
  ROW_NUMBER: 3,
  SELECTION_MARQUEE: 10,
  HEADER: 30,
  FIRST_COLUMN_HEADER: 31,
  ROW_NUMBER_HEADER: 32,
  CONTEXT_MENU: 50,
  HOVER_PREVIEW: 100,
  DELETE_MODAL: 200,
  DELETE_MODAL_CONTENT: 201,
  ADD_COLUMN_MODAL: 1100,
  ENRICHMENT_DETAILS_MODAL: 999,
  ENRICHMENT_DETAILS_CONTENT: 998,
} as const;

// Keyboard navigation constants
export const KEYBOARD_NAV = {
  PAGE_JUMP_ROWS: 10, // Number of rows to jump on PageUp/PageDown
} as const;

// Hover preview constants
export const HOVER_PREVIEW = {
  MAX_WIDTH_RATIO: 0.4, // 40% of viewport width
  MAX_WIDTH_PX: 500,
  VIEWPORT_MARGIN: 16,
  ESTIMATED_HEIGHT: 200,
  GAP: 8,
} as const;

// Frozen column widths for scroll calculations
export const FROZEN_COLUMNS = {
  ROW_NUMBER: 40,
  NAME_COLUMN: 180,
  TOTAL: 220, // ROW_NUMBER + NAME_COLUMN
} as const;

// Helper to get column width based on column name
export const getColumnWidth = (columnName: string): number => {
  if (columnName === 'Name') return COLUMN_WIDTHS.NAME;
  const lowerName = columnName.toLowerCase();
  if (lowerName.includes('email') || lowerName === 'url') return COLUMN_WIDTHS.URL;
  return COLUMN_WIDTHS.DEFAULT;
};
